import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import DatePicker from "react-multi-date-picker";
import * as moment from "moment";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import { Dialog, Button, Dropdown, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import PulseLoader from "react-spinners/PulseLoader";
import { handleApiError } from "@utils/errorHandler";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import DocumentUpload from "@components/DocumentUpload/DocumentUpload";
import { disputeTypeOption } from "@utils/constants";
import DisputePlanning from "./components/DisputePlanning";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessagePara,
} from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";

import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  FlexRow,
  SectionContainer,
  GridRow,
  Labeltext,
} from "./AddEditDispute.styled";
import "./AddEditDispute.scss";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditDispute = (props) => {
  const {
    t,
    id,
    siteId,
    isEditMode,
    onClose,
    updateDisputeRefeshStatus,
    planningId,
    planningData,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isEntityCreating, setIsEntityCreating] = useState(false);
  const [createEntityAPIError, setCreateEntityAPIError] = useState("");
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [disputeDetails, setDisputeDetails] = useState("");
  const [disputeData, setDisputeData] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [centerList, setCenterList] = useState([]);
  const [haulierList, setHaulierList] = useState([]);
  const userInfo = UserInfo.getUser();

  const fetchDisputeData = () => {
    setIsLoading(true);
    axios
      .get(`/disputes/${id}`)
      .then((response) => {
        if (response.data?.[0]) {
          setDisputeData(response.data?.[0]);
        }
      })
      .catch((error) => handleApiError(error));
  };

  const fetchResponsibleCompany = () => {
    let url = `/disputes/responsibles`;
    if (siteId) {
      url = `${url}?jobsiteId=${siteId}`;
    }
    if (planningId && siteId) {
      url = `${url}&planningLineId=${planningId}`;
    }
    if (planningId && !siteId) {
      url = `${url}?planningLineId=${planningId}`;
    }
    axios
      .get(url)
      .then((response) => {
        if (response.data?.[0]) {
          if (response.data?.[0].client?.length > 0) {
            const clientData = response.data?.[0].client.map((data) => {
              return {
                label: data.name,
                value: data.id,
              };
            });
            setClientList(clientData);
          }
          if (response.data?.[0].centers?.length > 0) {
            const centerData = response.data?.[0].centers.map((data) => {
              return {
                label: data.name,
                value: data.id,
              };
            });
            setCenterList(centerData);
          }
          if (response.data?.[0].hauliers?.length > 0) {
            const haulierData = response.data?.[0].hauliers.map((data) => {
              return {
                label: data.name,
                value: data.id,
              };
            });
            setHaulierList(haulierData);
          }
        }
      })
      .catch((error) => handleApiError(error));
  };

  useEffect(() => {
    if (isEditMode) fetchDisputeData();
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) fetchResponsibleCompany();
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && disputeData?.id) fetchResponsibleCompany();
  }, [isEditMode, disputeData]);

  const [formData, setFormData] = useState({
    date: {
      required: true,
      value: disputeData?.date ?? "",
      error: "",
    },
    responsibilityType: {
      required: true,
      value: disputeData?.type ?? "",
      error: "",
    },
    company: {
      required: true,
      value: disputeData?.responsible?.id ?? "",
      error: "",
    },
    fees: {
      required: true,
      value: disputeData?.fees ?? "",
      error: "",
    },
    details: {
      required: true,
      value: "",
      error: "",
    },
    documents: [],
  });

  useEffect(() => {
    if (isEditMode && disputeData?.id) {
      const formUpdatedInfo = {
        date: {
          required: true,
          value: moment(disputeData?.date).format("YYYY-MM-DD") ?? "",
          error: "",
        },
        responsibilityType: {
          required: true,
          value: disputeData?.type ?? "",
          error: "",
        },
        company: {
          required: true,
          value: disputeData?.responsible?.id ?? "",
          error: "",
        },
        fees: {
          required: true,
          value: disputeData?.fees ?? "",
          error: "",
        },
        details: {
          required: true,
          value: disputeData?.details ? disputeData?.details : "",
          error: "",
        },
        documents: [],
      };
      setFormData(formUpdatedInfo);
      setDisputeDetails(disputeData?.details ? disputeData?.details : "");
      setIsLoading(false);
    }
  }, [isEditMode, disputeData]);

  useEffect(() => {
    setFormData({
      ...formData,
      details: {
        value: disputeDetails,
        error: "",
        required: true,
      },
    });
  }, [disputeDetails]);

  const getCompanyOptions = () => {
    let optionTypes = [];
    if (parseInt(formData.responsibilityType.value, 10) === 1)
      optionTypes = clientList;
    if (parseInt(formData.responsibilityType.value, 10) === 2)
      optionTypes = centerList;
    if (parseInt(formData.responsibilityType.value, 10) === 3)
      optionTypes = haulierList;

    return optionTypes;
  };

  const getCompanyOptionsOnType = (type) => {
    let optionTypes = [];
    if (parseInt(type, 10) === 1) optionTypes = clientList;
    if (parseInt(type, 10) === 2) optionTypes = centerList;
    if (parseInt(type, 10) === 3) optionTypes = haulierList;

    return optionTypes;
  };

  const onDisputeDateChange = (e) => {
    setFormData({
      ...formData,
      date: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  const onResponsibilityTypeChange = (e) => {
    let companyValue = "";
    const optionTypes = getCompanyOptionsOnType(e.value);
    if (parseInt(e.value, 10) !== 4 && optionTypes?.length === 1) {
      companyValue = optionTypes?.[0]?.value;
    }
    setFormData({
      ...formData,
      responsibilityType: {
        value: e.value,
        error: "",
        required: true,
      },
      company: {
        value: companyValue,
        error: "",
        required: true,
      },
    });
  };

  const onCompanyChange = (e) => {
    setFormData({
      ...formData,
      company: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onFeesChange = (e) => {
    setFormData({
      ...formData,
      fees: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.date.value) {
      isFormValid = false;
      data.date.error = t("FIELD_REQUIRED");
    }
    if (!data.responsibilityType.value) {
      isFormValid = false;
      data.responsibilityType.error = t("FIELD_REQUIRED");
    }
    if (
      parseInt(data.responsibilityType.value, 10) !== 4 &&
      !data.company.value
    ) {
      isFormValid = false;
      data.company.error = t("FIELD_REQUIRED");
    }
    if (!data.details.value) {
      isFormValid = false;
      data.details.error = t("FIELD_REQUIRED");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    const dataModified = new FormData();
    dataModified.append("id", id);
    dataModified.append(
      "date",
      moment(formData.date.value).format("YYYY-MM-DD")
    );
    dataModified.append("type", formData.responsibilityType.value);
    dataModified.append(
      "site",
      JSON.stringify({
        id: siteId,
      })
    );
    if (planningId) {
      dataModified.append(
        "suppliersPlanningLine",
        JSON.stringify({
          id: planningId,
        })
      );
    }
    dataModified.append(
      "responsible",
      JSON.stringify({
        id:
          parseInt(formData.responsibilityType.value, 10) !== 4
            ? formData.company.value
            : "",
      })
    );
    dataModified.append("details", disputeDetails);
    dataModified.append("fees", formData.fees.value);
    dataModified.append(
      "createdBy",
      JSON.stringify({
        id: userInfo.id,
      })
    );
    dataModified.append(
      "updatedBy",
      JSON.stringify({
        id: userInfo.id,
      })
    );
    if (formData.documents.length > 0) {
      for (let i = 0; i < formData.documents.length; i++) {
        dataModified.append(
          `document[${i}][type]`,
          JSON.stringify({
            id: 5,
          })
        );
        dataModified.append(
          `document[${i}][filename]`,
          formData.documents[i].name
        );
        dataModified.append(`document[${i}][file]`, formData.documents[i]);
      }
    }
    if (existingDocuments.length > 0) {
      let i = formData.documents.length > 0 ? formData.documents.length : 0;
      existingDocuments.forEach((document) => {
        dataModified.append(`document[${i}][filename]`, document.fileName);
        dataModified.append(`document[${i}][id]`, document.id);
        dataModified.append(`document[${i}][file]`, document.fileName);
        dataModified.append(`document[${i}][type]`, 5);
        i += 1;
      });
    }
    return dataModified;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const data = getFormData(formData);
      setIsEntityCreating(true);
      setCreateEntityAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post(`/disputes`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsEntityCreating(false);
            updateDisputeRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsEntityCreating(false);
            setCreateEntityAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (id && isEditMode) {
        axios
          .post(`/disputes/${id}`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsEntityCreating(false);
            updateDisputeRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsEntityCreating(false);
            setCreateEntityAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <Dialog width="65rem">
      <div className="add-edit-dispute">
        <HeaderWrapper>
          <Title>
            {t("DISPUTE")}- {t("SITE")} # {siteId}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <FlexRow>
            <SectionContainer>
              <div>
                {isLoading && (
                  <div className="suggestions-list-loading">
                    <PulseLoader color="#1bace4" size={15} />
                  </div>
                )}
                {!isLoading && (
                  <>
                    {disputeData?.suppliersPlanningLine?.length > 0 && (
                      <DisputePlanning
                        planningLines={disputeData?.suppliersPlanningLine}
                      />
                    )}
                    {disputeData?.length === 0 && planningData?.length > 0 && (
                      <DisputePlanning planningLines={planningData} />
                    )}

                    <GridRow column="22% 32% 42%">
                      <div
                        className={
                          formData.date.error
                            ? "add-edit-dispute__date-container_start_error"
                            : "add-edit-dispute__date-container_start add-edit-dispute__date-container_start_required"
                        }
                      >
                        <div>
                          <label htmlFor="winning-date">
                            {t("DISPUTE_FORM_DATE")}
                          </label>
                        </div>
                        <DatePicker
                          inputClass={
                            formData.date.error
                              ? "add-edit-dispute__date-input-error"
                              : "add-edit-dispute__date-input"
                          }
                          maxDate={moment().toDate()}
                          containerClassName="add-edit-dispute__datepicker-container"
                          type="text"
                          label=""
                          name="add-edit-dispute-start-date"
                          id="add-edit-dispute-start-date"
                          calendarPosition="bottom-center"
                          isFloat
                          onChange={onDisputeDateChange}
                          format={getDatePickerFormat()}
                          locale={getCalendarLocale()}
                          value={
                            getDateFormat(formData.date.value, "YYYY-MM-DD") ||
                            ""
                          }
                          containerStyles={{ marginTop: "21px" }}
                          labelStyles={
                            formData.date.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          required
                          error={formData.date.error}
                          placeholder={t("DISPUTE_FORM_DATE")}
                        />
                        {formData.date.error && (
                          <RequiredMessagePara>
                            {formData.date.error}
                          </RequiredMessagePara>
                        )}
                      </div>
                      <Dropdown
                        id="dispute-responsibilty-tyoe"
                        label={t("DISPUTE_FORM_RESPONSIBILITY")}
                        name="dispute-responsibilty-tyoe"
                        value={formData.responsibilityType.value}
                        onChange={onResponsibilityTypeChange}
                        defaultOption={t("DISPUTE_FORM_RESPONSIBILITY")}
                        options={disputeTypeOption(t)}
                        labelStyles={
                          formData.responsibilityType.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputStyles={
                          formData.responsibilityType.error !== ""
                            ? inputStylesError
                            : inputStyles
                        }
                        required
                        error={formData.responsibilityType.error}
                      />
                      {parseInt(formData.responsibilityType.value, 10) !==
                        4 && (
                        <Dropdown
                          id="dispute-company"
                          label={t("DISPUTE_FORM_RESPONSIBLE")}
                          name="dispute-company"
                          value={formData.company.value}
                          onChange={onCompanyChange}
                          defaultOption={t("DISPUTE_FORM_RESPONSIBLE")}
                          options={getCompanyOptions(
                            formData.responsibilityType.value
                          )}
                          labelStyles={
                            formData.company.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.company.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          required
                          error={formData.company.error}
                        />
                      )}
                    </GridRow>

                    <GridRow
                      column="1fr"
                      gridGap="5px"
                      className={
                        formData.details.error === ""
                          ? "add-edit-dispute__title"
                          : "add-edit-dispute__title_error"
                      }
                    >
                      <Labeltext>{t("DISPUTE_FORM_DETAILS")}</Labeltext>
                      <CKEditorText
                        descriptionData={disputeDetails}
                        setDescriptionData={setDisputeDetails}
                      />
                      {formData.details.error && (
                        <RequiredMessagePara>
                          {formData.details.error}
                        </RequiredMessagePara>
                      )}
                    </GridRow>
                    <GridRow column="1fr">
                      <TextInput
                        id="dispute-fees"
                        type="text"
                        label={t("DISPUTE_FORM_FEES")}
                        isFloat
                        value={String(formData.fees.value)}
                        onChange={(e) => onFeesChange(e)}
                        containerStyles={{ marginTop: "21px" }}
                        errorStyles={{ "font-style": "italic" }}
                        labelStyles={
                          formData.fees.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputStyles={
                          formData.fees.error !== ""
                            ? inputStylesError
                            : inputStyles
                        }
                        error={formData.fees.error}
                        placeholder={t("DISPUTE_FORM_FEES")}
                      />
                    </GridRow>
                    <GridRow gridGap="0rem">
                      <DocumentUpload
                        isEditMode={isEditMode}
                        setFormData={setFormData}
                        formData={formData}
                        selectedProduct={disputeData}
                        existingDocuments={existingDocuments}
                        setExistingDocuments={setExistingDocuments}
                      />
                    </GridRow>
                  </>
                )}
              </div>
            </SectionContainer>
          </FlexRow>
        </FormBody>

        <ButtonWrapper>
          {createEntityAPIError && <div>{createEntityAPIError}</div>}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              marginRight: "0.32rem",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CANCEL")}
          </Button>
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isEntityCreating && <ClipLoader size={15} color="#fff" />}
              {!isEntityCreating && t("VALIDATE")}
            </>
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditDispute.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateDisputeRefeshStatus: PropTypes.func.isRequired,
  siteId: PropTypes.number.isRequired,
  planningId: PropTypes.number,
};

AddEditDispute.defaultProps = {
  id: null,
  isEditMode: false,
  planningId: null,
};

export default withTranslation()(AddEditDispute);
